/**
 * Sprint 5-b · 會議室連續 STT（Web Speech·continuous + interim）。
 * 內建 onend 自動重啟（繞過瀏覽器 ~60s auto-stop·SPEC session-timeout mitigation）·
 * final 段落回 onFinal，interim 供 realtime 顯示。不支援 → supported=false（UI 退回文字）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type SR = any;

const getRecognition = (): SR | null => {
  const w = window as any;
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
};

export interface UseContinuousSpeech {
  supported: boolean;
  recording: boolean;
  interim: string;
  start: (onFinal: (text: string) => void) => void;
  stop: () => void;
}

export function useContinuousSpeech(lang = 'zh-TW'): UseContinuousSpeech {
  const [supported] = useState(() => !!getRecognition());
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState('');
  const recRef = useRef<SR | null>(null);
  const wantRef = useRef(false);           // 使用者是否仍要錄（onend 自動重啟依此）
  const finalCb = useRef<(t: string) => void>(() => {});

  const _spin = useCallback(() => {
    const rec = getRecognition();
    if (!rec) return;
    recRef.current = rec;
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (ev: any) => {
      let itm = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        const txt = r[0]?.transcript || '';
        if (r.isFinal) finalCb.current(txt.trim());
        else itm += txt;
      }
      setInterim(itm);
    };
    rec.onend = () => {
      setInterim('');
      if (wantRef.current) {              // 仍要錄 → 無縫重啟
        try { rec.start(); } catch { setTimeout(() => { if (wantRef.current) _spin(); }, 300); }
      } else {
        setRecording(false);
      }
    };
    rec.onerror = (e: any) => {
      // no-speech / aborted 屬正常 → 由 onend 重啟；其他錯誤也讓 onend 處理
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
        wantRef.current = false; setRecording(false);
      }
    };
    try { rec.start(); } catch { /* onend 會補 */ }
  }, [lang]);

  const start = useCallback((onFinal: (text: string) => void) => {
    finalCb.current = onFinal;
    wantRef.current = true;
    setRecording(true);
    _spin();
  }, [_spin]);

  const stop = useCallback(() => {
    wantRef.current = false;
    setRecording(false);
    setInterim('');
    try { recRef.current?.stop?.(); } catch { /* noop */ }
  }, []);

  useEffect(() => () => { wantRef.current = false; try { recRef.current?.abort?.(); } catch { /* noop */ } }, []);

  return { supported, recording, interim, start, stop };
}
